import { Component } from 'react'
import PropTypes from 'prop-types'
import { engagementTracker } from '../../../services/engagement-tracking/engagementTracker.js'
import * as videoEvents from '../../../services/engagement-tracking/videoTrackingEvents.js'
import { adDecisionToPauseOverlay } from '../../ads/helpers/adDecisionToPauseOverlay.js'
import { extractVastInfo } from '../../ads/helpers/extractVastInfo.js'
import Player from '../Player'

const SEEK_TIME_UPDATE_INTERVAL = 10 // seconds

const urlIsValid = (video) => video && (!video.expiresAt || Date.now() < video.expiresAt * 1000)

/**
 * The `PlaylistPlayer` connects the playlist data provided by the API client to
 * a player with the `Player` interface.
 */
class PlaylistPlayer extends Component {
  static defaultProps = {
    height: '100%',
    width: '100%',
  }

  state = {
    playlistIndex: 0,
    video: this.props.playlist[0],
    pauseOverlayLeft: [],
    pauseOverlayRight: [],
  }

  videoId = null
  duration = 0
  seekTime = 0
  lastTimeUpdate = 0
  vastAds = []
  bannerAds = null

  componentDidMount() {
    this.queueNewVideo()
  }

  componentDidUpdate(prevProps, prevState) {
    if (prevProps.playlist !== this.props.playlist) {
      this.setState({ playlistIndex: 0 })
    }

    if (prevState.playlistIndex !== this.state.playlistIndex) {
      this.queueNewVideo()
    }
  }

  componentWillUnmount() {
    this.trackStartTimeout
      ? clearTimeout(this.trackStartTimeout)
      : this.trackEvent(videoEvents.VIDEO_CLOSED)
  }

  async queueNewVideo() {
    const { playlist, onQueueNewVideo } = this.props
    const nextVideo = playlist[this.state.playlistIndex]

    // For tracking the full path is used as the videoId
    const urlPathStart = nextVideo.url.indexOf('/', 9) // Search after https://
    const urlSearchStart = nextVideo.url.indexOf('?')
    this.videoId = nextVideo.url.substring(
      urlPathStart,
      urlSearchStart === -1 ? undefined : urlSearchStart,
    )

    this.lastTimeUpdate = this.seekTime = nextVideo.seekTime || 0
    const {
      vast = [],
      pauseOverlayLeft = [],
      pauseOverlayRight = [],
    } = nextVideo.adsV2 ?? nextVideo.ads ?? {}
    // Clone ads to avoid mutating the original playlist
    this.vastAds = vast.map((ad) => ({ ...ad }))
    this.bannerAds = {
      pauseOverlayLeft: pauseOverlayLeft.map((ad) => ({ ...ad })),
      pauseOverlayRight: pauseOverlayRight.map((ad) => ({ ...ad })),
    }

    const refreshedVideo = await this.refreshUrlIfInvalid(nextVideo)
    this.setState((state) => (state.video !== refreshedVideo ? { video: refreshedVideo } : null))
    onQueueNewVideo && onQueueNewVideo(nextVideo)
  }

  handleVideoEnded = () => {
    this.trackEvent(videoEvents.VIDEO_ENDED)
    const playlistIndex = this.state.playlistIndex + 1
    this.props.playlist[playlistIndex]
      ? this.setState({ playlistIndex }, this.props.onNextVideo)
      : this.props.onRequestClose()
  }

  refreshUrlIfInvalid(video) {
    return this.props.onRequestRefreshUrl && !urlIsValid(video)
      ? this.props.onRequestRefreshUrl().then(({ url }) => ({ ...video, url }))
      : Promise.resolve(video)
  }

  async fetchPauseOverlayAdInfo(advert) {
    const { placement, fetched } = advert
    if (this.props.fetchBannerAdDecision && !fetched) {
      try {
        advert.fetched = true
        advert.static = await this.props
          .fetchBannerAdDecision(placement)
          .then(adDecisionToPauseOverlay)
      } catch (error) {
        advert.fetched = false
        throw error
      }
    }
    return advert.static
  }

  async fetchVastAdInfo(advert) {
    const { vastUrl, fetched } = advert
    if (!fetched) {
      try {
        advert.fetched = true
        const response = await fetch(vastUrl)
        const xml = await response.text()
        Object.assign(advert, extractVastInfo(xml))
      } catch (error) {
        advert.fetched = false
        throw error
      }
    }
    return advert
  }

  async switchToAd(advert) {
    advert.shown = true

    if (!advert.mediaFile) {
      return advert.errorUrl && fetch(advert.errorUrl).catch(this.props.notifyError)
    }

    this.setState({ advert })
    fetch(advert.impressionUrl).catch(this.props.notifyError)
    // Prefetch to show again after the ad
    const refreshedVideo = await this.refreshUrlIfInvalid(this.state.video)
    this.setState((state) => (state.video !== refreshedVideo ? { video: refreshedVideo } : null))
  }

  switchToFeature = async () => {
    const refreshedVideo = await this.refreshUrlIfInvalid(this.state.video)
    this.setState(({ video }) =>
      refreshedVideo === video ? { advert: null } : { advert: null, video: refreshedVideo },
    )
  }

  async updateBannerAds(bannerAdKey, playedSeconds) {
    const ads = await Promise.all(
      this.bannerAds[bannerAdKey]
        .filter((ad) => ad.fromTime <= playedSeconds && ad.toTime >= playedSeconds)
        .map((ad) => (ad.placement ? this.fetchPauseOverlayAdInfo(ad) : ad.static)),
    ).then((ads) => ads.filter(Boolean))

    this.setState((state) =>
      state[bannerAdKey].length !== ads.length ||
      !state[bannerAdKey].every((ad, i) => ad === ads[i])
        ? { [bannerAdKey]: ads }
        : null,
    )
  }

  handleDuration = (duration) => {
    this.duration = Math.floor(duration)
    // The duration is required before starting the video session.
    // Delay tracking the start in case it is cancelled almost immediately;
    // an example would be hovering posters in the film list which starts trailers.
    this.trackStartTimeout = setTimeout(() => {
      this.trackEvent(videoEvents.VIDEO_STARTED)
      this.trackStartTimeout = null
    }, 1500)
  }

  handleSecondsTick = (playedSeconds) => {
    this.seekTime = playedSeconds = Math.floor(playedSeconds)

    if (this.bannerAds) {
      this.updateBannerAds('pauseOverlayLeft', playedSeconds).catch(this.props.notifyError)
      this.updateBannerAds('pauseOverlayRight', playedSeconds).catch(this.props.notifyError)
    }

    // ChannelPlayer getCurrentAd prop to find pre-roll ads for immediate start with content.
    const advert = this.props.getCurrentAd
      ? this.props.getCurrentAd(this.vastAds)
      : this.vastAds?.find((ad) => Math.abs(ad.timeIndex / 1000 - playedSeconds) < 5)

    if (advert && !advert.shown) {
      const adInfoPromise = this.fetchVastAdInfo(advert).catch(this.props.notifyError)
      if (Math.round(advert.timeIndex / 1000) === playedSeconds || this.props.getCurrentAd) {
        // Ensure the ad info has loaded before switching
        return adInfoPromise.then(() => this.switchToAd(advert))
      }
    }

    this.props.onProgress?.(playedSeconds, this.duration)

    if (Math.abs(playedSeconds - this.lastTimeUpdate) >= SEEK_TIME_UPDATE_INTERVAL) {
      this.trackEvent(videoEvents.VIDEO_PROGRESS)
      if (this.duration) {
        this.props.onTimeUpdate?.(this.state.video.seekTimeType, playedSeconds, this.duration)
      }
    }
  }

  getEvent = (eventType, seekTime) => {
    const { video } = this.state
    const duration = video.durationSeconds ?? this.duration
    this.lastTimeUpdate = Math.floor(seekTime ?? this.seekTime)
    const event = {
      eventType,
      contentType: video.seekTimeType || 'misc',
      contentSlug: this.props.filmSlug || '',
      contentId: this.props.filmId || '',
      duration,
      videoId: this.videoId,
      seekTime: this.lastTimeUpdate,
      creditsStartSeconds: video.creditsStartSeconds,
      groupWatchId: this.props.groupWatchId,
      festivalId: this.props.festivalId,
    }
    if (!this.props.getEventData) {
      return event
    }
    const propsEvent = this.props.getEventData()
    if (!propsEvent || (!event.duration && !propsEvent.duration)) {
      return null
    }
    return {
      ...event,
      ...propsEvent,
    }
  }

  trackEvent = (eventType, seekTime) => {
    const event = this.getEvent(eventType, seekTime)
    if (event) {
      this.props.trackEvent ? this.props.trackEvent(event) : engagementTracker.logVideoEvent(event)
    }
  }

  onPause = () => this.trackEvent(videoEvents.VIDEO_PAUSED)
  onPlay = (seekTime) => this.trackEvent(videoEvents.VIDEO_PLAYED, seekTime)
  onScrubStarted = () => this.trackEvent(videoEvents.VIDEO_SCRUB_STARTED)
  onScrubEnded = (seekTime) => this.trackEvent(videoEvents.VIDEO_SCRUB_ENDED, seekTime)

  // Skip events are debounced at 800 ms
  onSkipped = (seekTime) => {
    clearTimeout(this.skippedTimeout)
    this.skippedTimeout = setTimeout(() => {
      this.trackEvent(videoEvents.VIDEO_SKIPPED, seekTime)
    }, 800)
    this.lastTimeUpdate = Math.floor(seekTime) // Set it now to prevent progress updates during debounce
  }

  render() {
    const { video, advert, pauseOverlayLeft, pauseOverlayRight } = this.state
    const {
      width,
      height,
      onStart,
      onRequestClose = () => null,
      onError,
      shownControlsHeight,
      shownControlsDuration,
      forceHLSSubtitles,
      mimeTypeMap,
      nativeHLS,
      fullscreenToggleSupported,
      userActiveOnKeydown,
      playbackStatusOverride,
      onPlaybackStatusChange,
      groupWatchChatEnabled,
      chatWidth,
      chatPosition,
      onPlaySuccess,
      playbackRateChangeEnabled,
      videoHidden,
      filmSlug,
      festivalId,
      initiallyMuted,
      onMuteToggle,
      notifyError,
      asChannelPlayer,
      isMobile,
      channelPlayerFullScreenWidth,
    } = this.props

    return (
      <Player
        width={width}
        height={height}
        title={video.title}
        type={video.seekTimeType}
        url={video.url}
        dash={video.dash}
        drm={video.drm}
        mime={(mimeTypeMap && mimeTypeMap[video.mime]) || video.mime}
        initialSeekTime={video.seekTime}
        duration={video.durationSeconds}
        onDuration={this.handleDuration}
        creditsStartSeconds={video.creditsStartSeconds}
        nextEpisode={video.nextEpisode}
        actors={video.actors}
        subtitles={video.subtitles}
        nativeHLS={nativeHLS}
        forceHLSSubtitles={forceHLSSubtitles}
        initialSubtitles={video.nonNativeEnglish ? 'en' : ''}
        initiallyMuted={initiallyMuted}
        onMuteToggle={onMuteToggle}
        shownControlsHeight={shownControlsHeight}
        shownControlsDuration={shownControlsDuration}
        onRequestClose={onRequestClose}
        onStart={onStart}
        onPlaySuccess={onPlaySuccess}
        onProgress={this.handleSecondsTick}
        onEnd={this.handleVideoEnded}
        onError={onError}
        notifyError={notifyError}
        advert={advert}
        onRequestCloseAdvert={this.switchToFeature}
        fullscreenToggleSupported={fullscreenToggleSupported}
        userActiveOnKeydown={userActiveOnKeydown}
        playbackStatusOverride={playbackStatusOverride}
        onPlaybackStatusChange={onPlaybackStatusChange}
        groupWatchChatEnabled={groupWatchChatEnabled}
        chatWidth={chatWidth}
        chatPosition={chatPosition}
        playbackRateChangeEnabled={playbackRateChangeEnabled}
        videoHidden={videoHidden}
        filmSlug={filmSlug}
        festivalId={festivalId}
        pauseOverlayLeft={pauseOverlayLeft}
        pauseOverlayRight={pauseOverlayRight}
        // Channel Player Mode
        asChannelPlayer={asChannelPlayer}
        isMobile={isMobile}
        channelPlayerFullScreenWidth={channelPlayerFullScreenWidth}
        // Event tracking
        onPlayed={this.onPlay}
        onPaused={this.onPause}
        onScrubStarted={this.onScrubStarted}
        onScrubEnded={this.onScrubEnded}
        onSkipped={this.onSkipped}
      >
        {this.props.children}
      </Player>
    )
  }
}

PlaylistPlayer.propTypes = {
  /** Width of the video player */
  width: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  /** Height of the video player */
  height: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  /** Set to `false` to force disabling all fullscreen toggle logic, useful for TV apps */
  fullscreenToggleSupported: PropTypes.bool,
  /** The amount by which to offset subtitles which the control bar is shown */
  shownControlsHeight: PropTypes.number,
  /** The duration for which controls are shown before auto-hiding */
  shownControlsDuration: PropTypes.number,
  /** Use HLS embedded subtitles even if sidecar VTT subtitles are available */
  forceHLSSubtitles: PropTypes.bool,
  /** The list of videos to play */
  playlist: PropTypes.arrayOf(
    PropTypes.shape({
      url: PropTypes.string.isRequired,
      mime: PropTypes.string.isRequired,
      dash: PropTypes.shape({
        url: PropTypes.string.isRequired,
        mime: PropTypes.string.isRequired,
      }),
      drm: PropTypes.object,
    }),
  ).isRequired,
  /** Called when the video first starts playing */
  onStart: PropTypes.func,
  /** Called every second as the current progress / seek time changes, `(seekTime, duration) => {}` */
  onProgress: PropTypes.func,
  /** Called every 10 seconds as the current progress / seek time changes, `('trailer' | 'feature', seekTime, duration) => {}` */
  onTimeUpdate: PropTypes.func,
  /** Called each time a new video in the playlist is started except for the first video, `() => {}` */
  onNextVideo: PropTypes.func,
  /** Called each time a new video in the playlist is started including the first video, `() => {}` */
  onQueueNewVideo: PropTypes.func,
  /** Called when user exits video */
  onRequestClose: PropTypes.func,
  /** Renew the signed URL used to fetch the master playlist */
  onRequestRefreshUrl: PropTypes.func,
  /** Called when there is a playback error */
  onError: PropTypes.func.isRequired,
  /** Override the video session event tracker */
  trackEvent: PropTypes.func,
  /** Called with `error` and `metaData` to log non-critical errors */
  notifyError: PropTypes.func.isRequired,
  /** Allows playback to be controlled from parent components */
  playbackStatusOverride: PropTypes.shape({
    playing: PropTypes.bool.isRequired,
    seekTime: PropTypes.number.isRequired,
  }),
  /** Called when user plays, pauses or skips */
  onPlaybackStatusChange: PropTypes.func,
  /** Whether using the keyboard is considered user activity (e.g. we may want to ignore key presses in Group Watch chat) */
  userActiveOnKeydown: PropTypes.bool,
  /** Whether the user may modify the video playback rate */
  playbackRateChangeEnabled: PropTypes.bool,
  /** Whether the video component is displayed */
  videoHidden: PropTypes.bool,
  /** ID of the current film, used for tracking */
  filmId: PropTypes.string,
  /** Slug of the current film */
  filmSlug: PropTypes.string,
  /** Group watch ID of the current session */
  groupWatchId: PropTypes.string,
  /** Festival ID of the current film */
  festivalId: PropTypes.string,
  /** Specify a placement config to fetch an ad decision for */
  fetchBannerAdDecision: PropTypes.func,
  /** Get alternative tracking data for source that need different values, such as channels */
  getEventData: PropTypes.func,
  /** Render prop for player controls, `videoControlsProps => <PlayerControls {...videoControlsProps} />` */
  children: PropTypes.func.isRequired,
}

export default PlaylistPlayer
